import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { marked } from "marked";
import DOMPurify from "dompurify";

export const downloadMarkdownAsPDF = async (
	markdownPath,
	fileName,
	isCompact = false,
) => {
	try {
		// Fetch the markdown file
		const response = await fetch(markdownPath);
		const markdownText = await response.text();

		// Convert markdown to HTML with tables enabled
		marked.setOptions({
			breaks: true,
			gfm: true, // GitHub Flavored Markdown (includes tables)
		});
		const htmlContent = marked(markdownText);

		// Create a temporary div to parse HTML
		const sanitizedHtml = DOMPurify.sanitize(htmlContent);
		const tempDiv = document.createElement("div");
		tempDiv.innerHTML = sanitizedHtml;

		// Create PDF with compact settings for Key Facts (single-sided FCA requirement)
		const pdf = new jsPDF("p", "mm", "a4");
		const pageWidth = pdf.internal.pageSize.getWidth();
		const pageHeight = pdf.internal.pageSize.getHeight();
		const margin = isCompact ? 10 : 15;
		const maxWidth = pageWidth - margin * 2;
		let yPosition = margin;

		// Process each element
		const elements = tempDiv.children;

		for (let i = 0; i < elements.length; i++) {
			const element = elements[i];
			const tagName = element.tagName.toLowerCase();

			// Handle tables
			if (tagName === "table") {
				const tableData = [];
				const headers = [];

				// Extract headers
				const thead = element.querySelector("thead");
				if (thead) {
					const headerRow = thead.querySelector("tr");
					if (headerRow) {
						const ths = headerRow.querySelectorAll("th");
						ths.forEach((th) => headers.push(th.textContent.trim()));
					}
				}

				// Extract body rows
				const tbody = element.querySelector("tbody");
				if (tbody) {
					const rows = tbody.querySelectorAll("tr");
					rows.forEach((row) => {
						const rowData = [];
						const cells = row.querySelectorAll("td");
						cells.forEach((cell) => rowData.push(cell.textContent.trim()));
						if (rowData.length > 0) {
							tableData.push(rowData);
						}
					});
				}

				// Check if we need a new page for the table
				if (yPosition > pageHeight - 60) {
					pdf.addPage();
					yPosition = margin;
				}

				// Add table using autoTable with compact settings for Key Facts
				autoTable(pdf, {
					head: headers.length > 0 ? [headers] : undefined,
					body: tableData,
					startY: yPosition,
					margin: { left: margin, right: margin },
					styles: {
						fontSize: isCompact ? 7 : 10,
						cellPadding: isCompact ? 1.5 : 3,
					},
					headStyles: {
						fillColor: [51, 132, 188], // Blue color
						textColor: 255,
						fontStyle: "bold",
						fontSize: isCompact ? 7 : 10,
					},
					alternateRowStyles: {
						fillColor: [245, 245, 245],
					},
					theme: "grid",
				});

				yPosition = pdf.lastAutoTable.finalY + (isCompact ? 3 : 10);
				continue;
			}

			// Handle lists
			if (tagName === "ul" || tagName === "ol") {
				const listItems = element.querySelectorAll("li");
				pdf.setFontSize(isCompact ? 8 : 11);
				pdf.setFont(undefined, "normal");

				listItems.forEach((li, index) => {
					const bullet = tagName === "ul" ? "• " : `${index + 1}. `;
					const text = li.textContent.trim();
					const lines = pdf.splitTextToSize(bullet + text, maxWidth - 5);

					const lineHeight = pdf.getLineHeight() / pdf.internal.scaleFactor;
					const blockHeight = lines.length * lineHeight;

					if (yPosition + blockHeight > pageHeight - margin) {
						pdf.addPage();
						yPosition = margin;
					}

					pdf.text(lines, margin + 5, yPosition);
					yPosition += blockHeight + (isCompact ? 1 : 3);
				});

				yPosition += isCompact ? 2 : 5; // Extra spacing after list
				continue;
			}

			// Handle regular text elements
			let text = element.textContent.trim();
			if (!text) continue;

			// Set font based on element type with compact sizes for Key Facts
			if (tagName === "h1") {
				pdf.setFontSize(isCompact ? 12 : 20);
				pdf.setFont(undefined, "bold");
				yPosition += isCompact ? 2 : 5; // Extra space before heading
			} else if (tagName === "h2") {
				pdf.setFontSize(isCompact ? 10 : 16);
				pdf.setFont(undefined, "bold");
				yPosition += isCompact ? 2 : 5;
			} else if (tagName === "h3") {
				pdf.setFontSize(isCompact ? 9 : 14);
				pdf.setFont(undefined, "bold");
				yPosition += isCompact ? 1.5 : 3;
			} else if (tagName === "h4") {
				pdf.setFontSize(isCompact ? 8.5 : 12);
				pdf.setFont(undefined, "bold");
				yPosition += isCompact ? 1.5 : 3;
			} else if (
				element.querySelector("strong") ||
				element.querySelector("b")
			) {
				pdf.setFontSize(isCompact ? 8 : 11);
				pdf.setFont(undefined, "bold");
			} else {
				pdf.setFontSize(isCompact ? 8 : 11);
				pdf.setFont(undefined, "normal");
			}

			// Split text to fit page width
			const lines = pdf.splitTextToSize(text, maxWidth);

			// Check if we need a new page
			const lineHeight = pdf.getLineHeight() / pdf.internal.scaleFactor;
			const blockHeight = lines.length * lineHeight;

			if (yPosition + blockHeight > pageHeight - margin) {
				pdf.addPage();
				yPosition = margin;
			}

			// Add text to PDF
			pdf.text(lines, margin, yPosition);
			yPosition += blockHeight + (isCompact ? 2 : 5); // Add spacing after block
		}

		// Save the PDF
		pdf.save(fileName);
	} catch (error) {
		console.error("Error generating PDF:", error);
		alert("Failed to generate PDF. Please try again.");
	}
};
